import ConsumerRefService from "@api/consumer/consumerRefService";
import { GoalRewardType, IGoalBalanceReward, IGoalItemReward, IGoalReward } from "@interfaces/consumer/refs";
import { useItem } from "@hooks/selectors/user/item.selectors";
import { FC, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { ColumnDef } from "@tanstack/react-table";
import { useRemoteTable } from "@hooks/tableHooks";
import { Card, Form } from "react-bootstrap";
import { Flex, IconButton } from "@components/falcon/common";
import { AdvanceTable, AdvanceTableFooter, AdvanceTableSearchBox } from "@components/falcon/common/tables";
import AdvanceTableProvider from "@components/falcon/common/tables/context";
import { useRole } from "@services/hooks";

const RewardInfoCell: FC<{ reward: IGoalReward }> = ({ reward }) => {
    const balance = (reward as IGoalBalanceReward).balance;
    const itemId = (reward as IGoalItemReward).itemId;
    const count = (reward as IGoalItemReward).count;

    const item = useItem(itemId || '');

    const info = reward.type === GoalRewardType.Balance ? `${balance} ₽` : `${item.name} x${count}`;

    return <span>{info}</span>;
};

const columns: ColumnDef<IGoalReward>[] = [
    {
        accessorKey: 'name',
        header: 'Название',
        enableSorting: true,
        meta: {
            headerProps: { className: 'pe-1 text-900' },
            cellProps: { className: 'py-2' }
        },
        cell: ({ row: { original: { id, goalId, name } } }) => (
            <Link to={`/consumer/goals/${goalId}/rewards/${id}/`}>
                <Flex alignItems="center" className="flex-1">
                    <h5 className="mb-0 fs-10">{name}</h5>
                </Flex>
            </Link>
        )
    },
    {
        accessorKey: 'type',
        header: 'Тип',
        enableSorting: false,
        cell: ({ row: { original: { type } } }) => type === GoalRewardType.Balance ? 'Баланс' : 'Предмет',
    },
    {
        accessorKey: 'info',
        header: 'Награда',
        enableSorting: false,
        cell: ({ row: { original } }) => (
            <RewardInfoCell reward={original} />
        ),
    },
    {
        accessorKey: 'isActive',
        header: 'Активно',
        enableSorting: false,
        meta: {
            headerProps: { className: 'text-center text-900' },
            cellProps: { className: 'text-center' }
        },
        cell: ({ row: { original: { isActive } } }) => (
            <div className="text-center">
                <Form.Check type="checkbox" defaultChecked={isActive} disabled />
            </div>
        )
    }
]

const GoalRewardsList: FC = () => {

    const [api] = useState(() => new ConsumerRefService());
    const navigate = useNavigate();
    const { goal_id } = useParams<{ goal_id: string }>();

    const table = useRemoteTable({
        name: `consumer.goals.${goal_id}.rewards`,
        columns,
        defaultState: {
            sorting: [{ id: 'name', desc: false }]
        }
    }, (model) => api.getGoalRewards(goal_id || '', model));

    const canEdit = useRole('consumer.goal.edit');

    return (
        <AdvanceTableProvider table={table}>
            <Card className="mb-3">
                <Card.Header>
                    <Flex className='align-items-center justify-content-between'>
                        <Flex alignItems="center" className="fs-10">
                            <h5 className="fs-9 mb-0 text-nowrap py-2 py-xl-0">Список наград</h5>
                        </Flex>
                        <Flex>
                            {canEdit && (
                                <IconButton
                                    variant="falcon-default"
                                    size="sm"
                                    icon="plus"
                                    transform="shrink-3"
                                    className='me-2'
                                    onClick={() => navigate(`/consumer/goals/${goal_id}/rewards/add/`)}
                                >
                                    <span className="d-none d-sm-inline-block ms-1">Создать</span>
                                </IconButton>
                            )}
                            <Flex><AdvanceTableSearchBox /></Flex>
                        </Flex>
                    </Flex>
                </Card.Header>
                <Card.Body className="p-0">
                    <AdvanceTable
                        headerClassName="bg-200 text-nowrap align-middle"
                        rowClassName="align-middle white-space-nowrap"
                        tableProps={{
                            size: 'sm',
                            striped: true,
                            className: 'fs-10 mb-0 overflow-hidden'
                        }}
                    />
                </Card.Body>
                <Card.Footer>
                    <AdvanceTableFooter
                        rowInfo
                        rowsPerPageSelection
                        paginationButtons
                    />
                </Card.Footer>
            </Card>
        </AdvanceTableProvider>
    )
}

export default GoalRewardsList;
